import { FeMap } from '@fe/maplibre-extensions';
import { MapMouseEvent } from 'maplibre-gl';
import { WeatherPickingInfoLayer } from '../manager/types';
import { WeatherSettings, WeatherSettingsChangeSource } from '../settings/settings';
import { Dispatcher } from '../worker/dispatcher';
import { WeatherStoreProductInfo } from '../worker/store/weather-store';
import { SlotLayer } from './custom/slot/slot-layer';

/**
 * Base class for all fe-weather layers
 */
export abstract class WeatherLayerBase {
    /** The base id which all sub layers must start with */
    protected readonly baseId: string;
    /** The top slot for this layer */
    protected readonly slotTopLayerId: string;
    /** The bottom  slot for this layer */
    protected readonly slotBottomLayerId: string;
    /** Weather product */
    protected info: WeatherStoreProductInfo | null = null;
    /** Currently visible frame index */
    protected visibleFrame: number | null = null;
    /** If the layer is attached to the map */
    protected attached = false;
    /** Layer visible or hidden from weather st ack */
    protected visible: boolean = false;
    /** Dispatcher to be used by the layer */
    protected dispatcher: Dispatcher;
    /** Weather settings */
    private _settings: WeatherSettings;
    private _productLoaded = false;

    // TODO: add abort controller to loadProduct and loadFrame methods

    /** Weather settings */
    protected get settings() {
        return this._settings;
    }

    constructor(
        protected readonly map: FeMap,
        protected readonly workspaceId: number,
        dispatcher: Dispatcher,
        protected readonly productId: number,
        protected readonly weatherTopSlot: string,
        settings: WeatherSettings,
        subLayerName?: string
    ) {
        this.dispatcher = dispatcher;
        this._settings = settings;
        this.baseId = 'fe-weather-' + productId.toString();
        if (subLayerName) {
            this.baseId += '-' + subLayerName;
        }

        this.slotTopLayerId = this.baseId + '-slot-top';
        this.slotBottomLayerId = this.baseId + '-slot-bottom';
    }

    /**
     * Called when the layer is added to the stack
     */
    add(): void {
        if (this.attached) {
            return;
        }

        // Add slots, this allows sub layers to insert in the right place in the stack
        this.map.addLayer(new SlotLayer(this.slotTopLayerId), this.weatherTopSlot);
        this.map.addLayer(new SlotLayer(this.slotBottomLayerId), this.slotTopLayerId);
        this.map.setLayoutProperty(this.slotTopLayerId, 'visibility', 'none');
        this.map.setLayoutProperty(this.slotBottomLayerId, 'visibility', 'none');
        this.attached = true;
        this.onAdd();
        for (const layer of this.map.getLayersOrder().filter((x) => x.startsWith(this.baseId))) {
            this.map.setLayoutProperty(layer, 'visibility', 'none');
        }
    }

    /**
     * Called when the layer is removed from the stack
     */
    remove(): void {
        if (!this.attached) {
            return;
        }

        this.onRemove();
        // Remove the slots as well
        this.map.removeLayer(this.slotTopLayerId);
        this.map.removeLayer(this.slotBottomLayerId);
        this.attached = false;
    }

    /**
     * Called both when the product has been loaded and updated
     * @param info Product information
     */
    async productUpdated(info: WeatherStoreProductInfo) {
        this.info = info;
        try {
            await this.loadProduct();
        } catch (error) {
            console.error(`Product loading error: %s`, this.baseId);
            console.error(error);
            return;
        }

        this._productLoaded = true;

        // Reload current frame if any
        if (this.visibleFrame !== null) {
            try {
                await this.loadFrame(this.visibleFrame);
            } catch (error) {
                console.error('Error loading frame %d in %s', this.visibleFrame, this.baseId);
                console.error(error);
            }
        }
    }

    /**
     * Sets currently visible frame
     * @param frameId The id of the frame to show
     */
    async setVisibleFrame(frameId: number | null): Promise<void> {
        if (this.visibleFrame === frameId) {
            return;
        }

        const previousShouldDisplay = this.shouldDisplay();

        this.visibleFrame = frameId;
        if (frameId !== null && this._productLoaded) {
            try {
                await this.loadFrame(frameId);
            } catch (error) {
                console.error('Error loading frame %d in %s', frameId, this.baseId);
                console.error(error);
                return;
            }
        }

        if (this.shouldDisplay() !== previousShouldDisplay) {
            this.onDisplayChanged();
        }
    }

    /**
     * Gets visibility of this layer
     * @returns True if visible, otherwise false
     */
    shouldDisplay() {
        //return this.visible && this.enabled && this.visibleFrame !== null;
        return this.visible && this.visibleFrame !== null;
    }

    /**
     * The layer is visible when both visible and enabled are true.
     */
    setVisibility(visible: boolean) {
        if (this.visible === visible) {
            return;
        }
        const previousDisplay = this.shouldDisplay();
        this.visible = visible;
        if (previousDisplay !== this.shouldDisplay()) {
            this.onDisplayChanged();
        }
    }

    pickObjects(event: MapMouseEvent): Promise<WeatherPickingInfoLayer | WeatherPickingInfoLayer[] | null> {
        return Promise.resolve(null);
    }

    /**
     * Move this product and its sublayers
     * @param beforeId ID of the layer to move these layers before
     */
    move(beforeId?: string | undefined): void {
        const layers = this.map.getLayersOrder().filter((x) => x.startsWith(this.baseId));
        for (const layer of layers) {
            this.map.moveLayer(layer, beforeId ?? this.weatherTopSlot);
        }
    }

    /**
     * Gets the id of the bottom slot layer
     * @returns The id of bottom most layer (slot)
     */
    getBottomSlot(): string {
        return this.slotBottomLayerId;
    }

    updateSettings(settings: WeatherSettings, changeSource: WeatherSettingsChangeSource) {
        this._settings = settings;
        this.onSettingsUpdated(changeSource);
    }

    protected onAdd(): void {}
    protected onRemove(): void {}
    protected onDisplayChanged(): void {}
    protected loadProduct(): Promise<void> {
        return Promise.resolve();
    }
    protected loadFrame(frameId: number): Promise<void> {
        return Promise.resolve();
    }
    protected onSettingsUpdated(changeSource: WeatherSettingsChangeSource): void {}
}
